import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  IconButton,
  Badge
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Analytics as AnalyticsIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  ArrowForward as ArrowForwardIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  Web as WebIcon,
  Article as ArticleIcon,
  SupervisorAccount as SuperUserIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalApplications: '—',
    pendingReviews: '—',
    totalStudents: '—',
    totalStaff: '—',
    systemAlerts: 0,
    activeEvents: 0
  });

  React.useEffect(() => {
    const load = async () => {
      try {
        const [allApps, pendingApps, students, staff] = await Promise.allSettled([
          apiClient.getApplications({ limit: 1 }),
          apiClient.getApplications({ status: 'pending', limit: 1 }),
          apiClient.getUsers({ role: 'student', limit: 1 }),
          apiClient.getUsers({ role: 'teacher', limit: 1 }),
        ]);
        setStats(prev => ({
          ...prev,
          totalApplications: allApps.status === 'fulfilled' ? allApps.value.total : '—',
          pendingReviews:     pendingApps.status === 'fulfilled' ? pendingApps.value.total : '—',
          totalStudents:      students.status === 'fulfilled' ? students.value.total : '—',
          totalStaff:         staff.status === 'fulfilled' ? staff.value.total : '—',
        }));
      } catch {}
    };
    load();
  }, []);

  const adminModules = [
    {
      title: 'Form Configuration',
      description: 'Configure application forms, fields, and validation rules',
      icon: <AssignmentIcon />,
      color: '#050979',
      path: '/admin/form-config',
      status: 'active',
      lastUpdated: '2 days ago'
    },
    {
      title: 'Form Builder',
      description: 'Create new forms with drag and drop functionality',
      icon: <BuildIcon />,
      color: '#1976d2',
      path: '/admin/form-builder',
      status: 'active',
      lastUpdated: 'Just now'
    },
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: <PeopleIcon />,
      color: '#388e3c',
      path: '/admin/users',
      status: 'active',
      lastUpdated: '1 day ago'
    },
    {
      title: 'Student Records',
      description: 'View and manage student information and records',
      icon: <SchoolIcon />,
      color: '#f57c00',
      path: '/admin/students',
      status: 'active',
      lastUpdated: '3 hours ago'
    },
    {
      title: 'Application Reviews',
      description: 'Review and process student applications',
      icon: <AssignmentIcon />,
      color: '#7b1fa2',
      path: '/admin/applications',
      status: 'pending',
      lastUpdated: '30 minutes ago',
      badge: stats.pendingReviews
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide settings and preferences',
      icon: <SettingsIcon />,
      color: '#5d4037',
      path: '/admin/settings',
      status: 'active',
      lastUpdated: '1 week ago'
    },
    {
      title: 'Security & Access',
      description: 'Manage security settings and access controls',
      icon: <SecurityIcon />,
      color: '#d32f2f',
      path: '/admin/security',
      status: 'warning',
      lastUpdated: '2 days ago',
      badge: stats.systemAlerts
    },
    {
      title: 'Analytics & Reports',
      description: 'View system analytics and generate reports',
      icon: <AnalyticsIcon />,
      color: '#303f9f',
      path: '/admin/analytics',
      status: 'active',
      lastUpdated: '1 hour ago'
    },
    {
      title: 'Communication',
      description: 'Manage email templates and notifications',
      icon: <EmailIcon />,
      color: '#00796b',
      path: '/admin/communication',
      status: 'active',
      lastUpdated: '4 hours ago'
    },
    {
      title: 'Events & Calendar',
      description: 'Manage school events and calendar',
      icon: <EventIcon />,
      color: '#e64a19',
      path: '/admin/events',
      status: 'active',
      lastUpdated: '2 hours ago',
      badge: stats.activeEvents
    },
    {
      title: 'Financial Management',
      description: 'Manage tuition, fees, and financial records',
      icon: <PaymentIcon />,
      color: '#455a64',
      path: '/admin/finance',
      status: 'active',
      lastUpdated: '1 day ago'
    },
    {
      title: 'Data Management',
      description: 'Backup, restore, and manage system data',
      icon: <StorageIcon />,
      color: '#6a1b9a',
      path: '/admin/data',
      status: 'active',
      lastUpdated: '3 days ago'
    },
    {
      title: 'System Monitoring',
      description: 'Monitor system performance and health',
      icon: <DashboardIcon />,
      color: '#bf360c',
      path: '/admin/monitoring',
      status: 'active',
      lastUpdated: '15 minutes ago'
    },
    {
      title: 'Website Management',
      description: 'Edit homepage content, announcements, pages, and navigation',
      icon: <WebIcon />,
      color: '#00695c',
      path: '/admin/website',
      status: 'active',
      lastUpdated: '1 day ago'
    },
    {
      title: 'Super User Console',
      description: 'Direct data access, bulk operations, and database management',
      icon: <SuperUserIcon />,
      color: '#4a148c',
      path: '/admin/superuser',
      status: 'active',
      lastUpdated: '3 days ago',
      restricted: true
    }
  ];

  const recentActivity = [
    { action: 'New application submitted', user: 'John Smith', time: '5 minutes ago', type: 'application' },
    { action: 'Form configuration updated', user: 'Admin User', time: '2 hours ago', type: 'system' },
    { action: 'User role modified', user: 'Jane Doe', time: '4 hours ago', type: 'user' },
    { action: 'Security alert resolved', user: 'Security Team', time: '1 day ago', type: 'security' },
    { action: 'Backup completed successfully', user: 'System', time: '1 day ago', type: 'system' }
  ];

  const systemAlerts = [
    { message: 'Form validation rules need review', severity: 'warning', time: '2 hours ago' },
    { message: 'Database backup completed', severity: 'success', time: '1 day ago' },
    { message: 'Security update available', severity: 'info', time: '3 days ago' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'warning': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'pending': return <NotificationsIcon sx={{ fontSize: 16 }} />;
      case 'warning': return <WarningIcon sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all aspects of Eagles Refuge Academy
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {stats.totalApplications}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {stats.pendingReviews}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {stats.totalStudents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Students
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {stats.totalStaff}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Staff Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {stats.systemAlerts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System Alerts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="secondary.main">
                {stats.activeEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Admin Modules */}
        <Grid item xs={12} lg={8}>
          <Typography variant="h5" gutterBottom>
            Admin Modules
          </Typography>
          <Grid container spacing={2}>
            {adminModules.map((module, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate(module.path)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: module.color, mr: 2 }}>
                        {module.badge ? (
                          <Badge badgeContent={module.badge} color="error">
                            {module.icon}
                          </Badge>
                        ) : (
                          module.icon
                        )}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h3">
                          {module.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={module.status}
                            sx={{
                              bgcolor: getStatusColor(module.status),
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                            icon={getStatusIcon(module.status)}
                          />
                          {module.restricted && (
                            <Chip size="small" label="Super User" sx={{ bgcolor: '#4a148c', color: 'white', fontSize: '0.7rem', height: 20 }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {module.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last updated: {module.lastUpdated}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(module.path);
                      }}
                    >
                      Manage
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Recent Activity */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <List dense>
              {recentActivity.map((activity, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {activity.type === 'application' && <AssignmentIcon sx={{ fontSize: 16 }} />}
                        {activity.type === 'system' && <SettingsIcon sx={{ fontSize: 16 }} />}
                        {activity.type === 'user' && <PeopleIcon sx={{ fontSize: 16 }} />}
                        {activity.type === 'security' && <SecurityIcon sx={{ fontSize: 16 }} />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.action}
                      secondary={`${activity.user} • ${activity.time}`}
                    />
                  </ListItem>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          {/* System Alerts */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              System Alerts
            </Typography>
            <List dense>
              {systemAlerts.map((alert, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      {alert.severity === 'warning' && <WarningIcon color="warning" />}
                      {alert.severity === 'success' && <CheckCircleIcon color="success" />}
                      {alert.severity === 'info' && <NotificationsIcon color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.message}
                      secondary={alert.time}
                    />
                  </ListItem>
                  {index < systemAlerts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
